import {ApiClient} from '@hconnect/apiclient'
import {spawnErrorToast, spawnSuccessToast} from '@hm/fundamental'
import {useQueryClient, useMutation, type UseMutationOptions} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useTranslation} from 'react-i18next'

import {addCommentPath} from '../api'

type Props = {
  plantId: string
  commentsCategory: string
}

export type AddCommentParameter = {
  value: string
  commentCategoryInstanceId: string
}

export const useAddComment = (
  {plantId, commentsCategory}: Props,
  queryKeyPrefix: string = 'comments',
  options?: Omit<UseMutationOptions<void, AxiosError, AddCommentParameter>, 'mutationFn'>
) => {
  const {t} = useTranslation()
  const client = useQueryClient()

  return useMutation<void, AxiosError, AddCommentParameter>({
    ...options,
    mutationFn: async ({value, commentCategoryInstanceId}) => {
      const payload = {value}
      const path = addCommentPath({
        plantId,
        commentsCategory,
        commentCategoryInstanceId
      })

      await ApiClient.axiosInstance.post(path, payload)
    },
    onSuccess: (...args) => {
      spawnSuccessToast(t('comments.message.addSuccess'))
      void client.invalidateQueries({queryKey: [queryKeyPrefix]})
      options?.onSuccess?.(...args)
    },
    onError: (...args) => {
      spawnErrorToast(t('comments.message.failedToAddComment'))
      options?.onError?.(...args)
    },
    onSettled: (...args) => {
      options?.onSettled?.(...args)
    }
  })
}
